import React, { useState, useEffect, useMemo, useRef } from 'react';
import * as XLSX from 'xlsx';
import { Users, Plus, Edit2, Trash2, Save, X, Clock, Network, Table, Download, FileDown, Upload, Loader2, KeyRound } from 'lucide-react';
import FilterBar from './FilterBar';
import StaffScheduleModal from './StaffScheduleModal';
import StaffHierarchyTree from './StaffHierarchyTree';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AllStaff() {
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [masterRate, setMasterRate] = useState(null);
  const [newStaff, setNewStaff] = useState({
    name: '',
    mobile: '',
    staff_id: '',
    geo_fence_location_id: 'geo_carloo_ho'
  });
  const [editing, setEditing] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [scheduleStaff, setScheduleStaff] = useState(null);
  const [view, setView] = useState('table'); // 'table' | 'tree'
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [deptFilter, setDeptFilter] = useState('all');
  const [desigFilter, setDesigFilter] = useState('all');
  const [scheduleFilter, setScheduleFilter] = useState('all');
  const [geoFilter, setGeoFilter] = useState('all');
  const [departments, setDepartments] = useState([]);
  const [designations, setDesignations] = useState([]);
  const [schedules, setSchedules] = useState([]);
  const [geofences, setGeofences] = useState([]);

  const filteredStaff = useMemo(() => {
    const q = search.trim().toLowerCase();
    return staff.filter(s => {
      if (roleFilter !== 'all' && s.role !== roleFilter) return false;
      if (statusFilter !== 'all' && (s.status || 'active') !== statusFilter) return false;
      if (deptFilter !== 'all' && (s.department_id || '') !== deptFilter) return false;
      if (desigFilter !== 'all' && (s.designation_id || '') !== desigFilter) return false;
      if (scheduleFilter !== 'all' && (s.schedule_id || '') !== scheduleFilter) return false;
      if (geoFilter !== 'all' && (s.geo_fence_location_id || '') !== geoFilter) return false;
      if (q && !(
        (s.name || '').toLowerCase().includes(q) ||
        (s.mobile || '').toLowerCase().includes(q) ||
        (s.staff_id || '').toLowerCase().includes(q)
      )) return false;
      return true;
    });
  }, [staff, search, roleFilter, statusFilter, deptFilter, desigFilter, scheduleFilter, geoFilter]);

  useEffect(() => { fetchStaff(); fetchMasterRate(); fetchMasters(); }, []);

  const fetchMasters = async () => {
    try {
      const token = localStorage.getItem('token');
      const [d, dg, sc, gf] = await Promise.all([
        fetch(`${API_URL}/api/departments`, { headers: { 'Authorization': `Bearer ${token}` } }),
        fetch(`${API_URL}/api/designations`, { headers: { 'Authorization': `Bearer ${token}` } }),
        fetch(`${API_URL}/api/schedules`, { headers: { 'Authorization': `Bearer ${token}` } }),
        fetch(`${API_URL}/api/geofences`, { headers: { 'Authorization': `Bearer ${token}` } }),
      ]);
      if (d.ok) setDepartments(await d.json());
      if (dg.ok) setDesignations(await dg.json());
      if (sc.ok) setSchedules(await sc.json());
      if (gf.ok) setGeofences(await gf.json());
    } catch {}
  };

  const fetchMasterRate = async () => {
    try {
      const token = localStorage.getItem('token');
      const r = await fetch(`${API_URL}/api/admin/settings`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (r.ok) {
        const d = await r.json();
        setMasterRate(d.rate_per_km);
      }
    } catch (err) {
      console.error('Failed to fetch master rate:', err);
    }
  };

  const fetchStaff = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/admin/staff`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      setStaff(data.filter(s => s.status !== 'deleted'));
    } catch (err) {
      console.error('Failed to fetch staff:', err);
    } finally {
      setLoading(false);
    }
  };

  // ---- CSV helpers (template + export) ----
  const downloadCsv = (filename, headers, rows) => {
    const esc = (v) => {
      const s = v === null || v === undefined ? '' : String(v);
      return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const body = [headers, ...rows].map(r => r.map(esc).join(',')).join('\n');
    const blob = new Blob(['\uFEFF' + body], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = filename; a.click();
    URL.revokeObjectURL(url);
  };

  // Template with an example row so admins immediately see the expected format.
  const downloadTemplate = () => {
    const headers = ['Name', 'Staff ID', 'Mobile', 'Role', 'Department', 'Designation',
                     'Schedule', 'Report 1', 'Report 2', 'Report 3', 'GEO-FENCE', 'Rate/KM'];
    const example = ['John Doe', 'EMP001', '9999988888', 'staff', 'PRODUCTION', 'OPERATOR',
                     'HO Staff', 'Reporting Manager Name', '', '', 'Carloo HO', 2.25];
    const ws = XLSX.utils.aoa_to_sheet([headers, example]);
    ws['!cols'] = [{ wch: 22 }, { wch: 10 }, { wch: 14 }, { wch: 8 }, { wch: 16 }, { wch: 16 },
                   { wch: 14 }, { wch: 22 }, { wch: 22 }, { wch: 22 }, { wch: 18 }, { wch: 8 }];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'STAFF MASTER');
    XLSX.writeFile(wb, 'staff-template.xlsx');
  };

  const exportStaffList = () => {
    const headers = ['Name', 'Staff ID', 'Mobile', 'Role', 'Department', 'Designation', 'Schedule',
                     'Reports To 1', 'Reports To 2', 'Reports To 3', 'Geo-Fence Location', 'Status'];
    const nameOf = (id) => staff.find(x => x.id === id)?.name || '';
    const rows = filteredStaff.map(s => [
      s.name || '',
      s.staff_id || '',
      s.mobile || '',
      s.role || '',
      departments.find(d => d.id === s.department_id)?.name || '',
      designations.find(d => d.id === s.designation_id)?.name || '',
      schedules.find(sc => sc.id === s.schedule_id)?.name || '',
      nameOf(s.reports_to_1 || s.reporting_to),
      nameOf(s.reports_to_2),
      nameOf(s.reports_to_3),
      geofences.find(g => g.id === s.geo_fence_location_id)?.name || s.geo_fence_location_id || '',
      s.status || 'active',
    ]);
    const stamp = new Date().toISOString().slice(0, 10);
    downloadCsv(`staff-list-${stamp}.csv`, headers, rows);
  };

  // ===== Bulk import (Excel/CSV) =====
  const fileInputRef = useRef(null);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);

  const COL_ALIASES = {
    name: ['name', 'staff name', 'employee', 'employee name'],
    staff_id: ['staff id', 'staff_id', 'employee id', 'emp id', 'id'],
    mobile: ['mobile', 'phone', 'mobile number', 'contact', 'contact number'],
    role: ['role'],
    department: ['department', 'dept'],
    designation: ['designation', 'title'],
    schedule: ['schedule', 'shift', 'work schedule'],
    report_1: ['report 1', 'reports to 1', 'reports_to_1', 'reports to', 'reports_to', 'reporting to', 'manager'],
    report_2: ['report 2', 'reports to 2', 'reports_to_2'],
    report_3: ['report 3', 'reports to 3', 'reports_to_3'],
    geo_fence: ['geo-fence', 'geo fence', 'geofence', 'geo_fence', 'location'],
    rate_per_km: ['rate/km', 'rate per km', 'rate', 'per km rate', 'rate_per_km'],
  };
  const pickCol = (obj, key) => {
    const aliases = COL_ALIASES[key] || [key];
    for (const k of Object.keys(obj || {})) {
      const lower = String(k).trim().toLowerCase();
      if (aliases.includes(lower)) return obj[k];
    }
    return undefined;
  };
  const normaliseStaffRows = (raw) => raw.map(r => ({
    name: String(pickCol(r, 'name') ?? '').trim(),
    staff_id: String(pickCol(r, 'staff_id') ?? '').trim(),
    mobile: String(pickCol(r, 'mobile') ?? '').trim(),
    role: String(pickCol(r, 'role') ?? '').trim(),
    department: String(pickCol(r, 'department') ?? '').trim(),
    designation: String(pickCol(r, 'designation') ?? '').trim(),
    schedule: String(pickCol(r, 'schedule') ?? '').trim(),
    report_1: String(pickCol(r, 'report_1') ?? '').trim(),
    report_2: String(pickCol(r, 'report_2') ?? '').trim(),
    report_3: String(pickCol(r, 'report_3') ?? '').trim(),
    geo_fence: String(pickCol(r, 'geo_fence') ?? '').trim(),
    rate_per_km: parseFloat(pickCol(r, 'rate_per_km')) || null,
  })).filter(r => r.name && r.mobile);

  const parseStaffFile = async (file) => {
    const ext = file.name.split('.').pop().toLowerCase();
    if (ext === 'csv') {
      const text = await file.text();
      const cleaned = text.charCodeAt(0) === 0xFEFF ? text.slice(1) : text;
      const wb = XLSX.read(cleaned, { type: 'string' });
      const sheet = wb.Sheets[wb.SheetNames[0]];
      return normaliseStaffRows(XLSX.utils.sheet_to_json(sheet, { defval: '' }));
    }
    const buf = await file.arrayBuffer();
    const wb = XLSX.read(buf, { type: 'array' });
    const sheet = wb.Sheets[wb.SheetNames[0]];
    return normaliseStaffRows(XLSX.utils.sheet_to_json(sheet, { defval: '' }));
  };

  const handleStaffFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImporting(true); setImportResult(null);
    try {
      const rows = await parseStaffFile(file);
      if (!rows.length) {
        alert('File looks empty or missing required Name + Mobile columns.\n\nExpected headers (any case): Name, Staff ID, Mobile, Role, Department, Designation, Schedule, Report 1, Report 2, Report 3, GEO-FENCE, Rate/KM.');
        return;
      }
      if (!window.confirm(
        `Import ${rows.length} rows?\n\n` +
        `• Existing staff (matched by Mobile) will be UPDATED — passwords untouched.\n` +
        `• New staff will be CREATED — initial password = mobile number.\n\n` +
        `Schedules / geofences referenced by name must already exist in their masters; ` +
        `unknown ones are skipped per row and listed in warnings.`
      )) {
        return;
      }

      const CHUNK = 100;
      const totals = { created: 0, updated: 0, skipped_invalid: 0, total_received: 0, warnings: [] };
      const token = localStorage.getItem('token');
      for (let i = 0; i < rows.length; i += CHUNK) {
        const chunk = rows.slice(i, i + CHUNK);
        const r = await fetch(`${API_URL}/api/admin/staff/bulk-import`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({ rows: chunk, create_missing_masters: true }),
        });
        if (!r.ok) {
          const err = await r.json().catch(() => ({}));
          alert(`Import failed at batch ${i / CHUNK + 1} (HTTP ${r.status}): ${err.detail || ''}`);
          break;
        }
        const d = await r.json();
        totals.created += d.created || 0;
        totals.updated += d.updated || 0;
        totals.skipped_invalid += d.skipped_invalid || 0;
        totals.total_received += d.total_received || 0;
        if (Array.isArray(d.warnings)) totals.warnings.push(...d.warnings);
      }
      // De-dup warnings.
      totals.warnings = Array.from(new Set(totals.warnings));
      setImportResult(totals);
      fetchStaff();
      fetchMasters();
    } catch (err) {
      console.error(err);
      alert(`Could not read this file: ${err.message || err}`);
    } finally {
      setImporting(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleAddStaff = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${API_URL}/api/admin/staff?name=${encodeURIComponent(newStaff.name)}&mobile=${encodeURIComponent(newStaff.mobile)}&staff_id=${encodeURIComponent(newStaff.staff_id)}&geo_fence_location_id=${encodeURIComponent(newStaff.geo_fence_location_id)}`,
        { method: 'POST', headers: { 'Authorization': `Bearer ${token}` } }
      );
      const data = await response.json();
      if (response.ok) {
        alert(data.message);
        setShowAdd(false);
        setNewStaff({ name: '', mobile: '', staff_id: '', geo_fence_location_id: 'geo_carloo_ho' });
        fetchStaff();
      } else {
        alert(data.detail || 'Failed to add staff');
      }
    } catch (err) {
      alert('Error adding staff');
    }
  };

  const startEdit = (s) => {
    setEditing(s.id);
    setEditForm({
      name: s.name || '',
      staff_id: s.staff_id || '',
      mobile: (s.mobile || '').replace(/^\+91/, ''),  // raw 10-digit for the input
      reports_to_1: s.reports_to_1 || s.reporting_to || '',
      reports_to_2: s.reports_to_2 || '',
      reports_to_3: s.reports_to_3 || '',
      geo_fence_location_id: s.geo_fence_location_id || '',
      lunch_duration_hours: s.lunch_duration_hours || 1,
      department_id: s.department_id || '',
      designation_id: s.designation_id || '',
      schedule_id: s.schedule_id || '',
      status: s.status || 'active',
      role: s.role || 'staff',  // used in the table to gate the mobile input
    });
  };

  const saveEdit = async (id) => {
    const token = localStorage.getItem('token');
    const body = { ...editForm };
    ['reports_to_1', 'reports_to_2', 'reports_to_3', 'department_id', 'designation_id'].forEach(k => {
      if (!body[k]) body[k] = null;
    });
    body.lunch_duration_hours = parseFloat(body.lunch_duration_hours);

    // `role` is only stashed for UI gating — never send to the backend.
    const isAdmin = (body.role || '').toLowerCase() === 'admin';
    delete body.role;
    // Mobile is only sent for admin rows (server also enforces this).
    if (!isAdmin) {
      delete body.mobile;
    } else if (body.mobile !== undefined && body.mobile !== null) {
      body.mobile = String(body.mobile).trim();
      if (!body.mobile) {
        alert('Mobile cannot be blank for an admin.');
        return;
      }
    }

    // If the admin picked a schedule, call the schedule-assign endpoint first —
    // that copies all schedule fields onto the user. Then update the remaining
    // profile fields. schedule_id is kept out of the generic PUT.
    const chosenScheduleId = body.schedule_id || null;
    delete body.schedule_id;

    try {
      if (chosenScheduleId !== undefined) {
        await fetch(`${API_URL}/api/admin/staff/${id}/schedule`, {
          method: 'PUT',
          headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({ schedule_id: chosenScheduleId })
        });
      }
    } catch {}

    const r = await fetch(`${API_URL}/api/admin/staff/${id}`, {
      method: 'PUT',
      headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify(body)
    });
    if (r.ok) {
      setEditing(null);
      fetchStaff();
    } else {
      const d = await r.json();
      alert(d.detail || 'Update failed');
    }
  };

  const deleteStaff = async (s) => {
    if (!window.confirm(`Delete ${s.name}? This is a soft-delete; can be restored from DB.`)) return;
    const token = localStorage.getItem('token');
    const r = await fetch(`${API_URL}/api/admin/staff/${s.id}`, {
      method: 'DELETE',
      headers: { 'Authorization': `Bearer ${token}` }
    });
    if (r.ok) {
      fetchStaff();
    } else {
      const d = await r.json();
      alert(d.detail || 'Delete failed');
    }
  };

  // One-click password reset → password becomes the staff's mobile digits.
  // Useful when a staff forgets their password (esp. after a mobile-number
  // swap — May 29, 2026 ask). Admin reads the new password to the staff.
  const resetPasswordToMobile = async (s) => {
    if (!window.confirm(
      `Reset password for ${s.name}?\n\nNew password will be: ${(s.mobile || '').replace(/\D/g, '').slice(-10)}\n` +
      `(i.e. their 10-digit mobile number, no +91)\n\n` +
      `The staff must change it after logging in.`
    )) return;
    const token = localStorage.getItem('token');
    const r = await fetch(`${API_URL}/api/admin/staff/${s.id}/reset-password-to-mobile`, {
      method: 'POST',
      headers: { 'Authorization': `Bearer ${token}` }
    });
    const d = await r.json().catch(() => ({}));
    if (r.ok) {
      // Use prompt() so the admin can copy the password from the dialog
      // (alert() text isn't selectable on most Android keyboards).
      window.prompt(
        `Password reset for ${d.staff_name || s.name}.\n` +
        `Tell them to log in with:\n` +
        `  Mobile: ${(s.mobile || '').replace(/\D/g, '').slice(-10)}\n` +
        `  Password: (shown below — copy with the long-press menu)`,
        d.new_password
      );
    } else {
      alert(d.detail || 'Password reset failed');
    }
  };

  if (loading) return <div className="text-center py-8">Loading staff...</div>;

  return (
    <div data-testid="all-staff">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Staff & Admins ({staff.length})</h1>
        <div className="flex items-center gap-2">
          <div className="inline-flex bg-gray-100 rounded-lg p-1" data-testid="staff-view-toggle">
            <button
              onClick={() => setView('table')}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-semibold ${view === 'table' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
              data-testid="view-table-btn"
            >
              <Table size={14} /> Table
            </button>
            <button
              onClick={() => setView('tree')}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-semibold ${view === 'tree' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
              data-testid="view-tree-btn"
            >
              <Network size={14} /> Org Chart
            </button>
          </div>
          <button
            onClick={downloadTemplate}
            className="flex items-center gap-1.5 px-3 py-2 border border-gray-200 hover:bg-gray-50 text-gray-700 rounded-lg text-xs"
            data-testid="download-template-btn"
            title="Excel with the exact headers the bulk import expects"
          >
            <FileDown size={14} /> Template
          </button>
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv,.xlsx,.xls,.ods,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel"
            onChange={handleStaffFileUpload}
            className="hidden"
            data-testid="staff-bulk-import-input"
          />
          <button
            onClick={() => fileInputRef.current?.click()}
            disabled={importing}
            className="flex items-center gap-1.5 px-3 py-2 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-60 text-white rounded-lg text-xs"
            data-testid="staff-bulk-import-btn"
            title="Upload Excel/CSV — matches by Mobile, updates existing or creates new"
          >
            {importing ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
            {importing ? 'Importing…' : 'Bulk Import'}
          </button>
          <button
            onClick={exportStaffList}
            disabled={staff.length === 0}
            className="flex items-center gap-1.5 px-3 py-2 border border-gray-200 hover:bg-gray-50 text-gray-700 rounded-lg text-xs disabled:opacity-50"
            data-testid="export-staff-btn"
            title="Download the current staff list as CSV"
          >
            <Download size={14} /> Export
          </button>
          <button
            onClick={() => setShowAdd(!showAdd)}
            className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            data-testid="add-staff-btn"
          >
            <Plus size={18} /><span>Add Staff</span>
          </button>
        </div>
      </div>

      {importResult && (
        <div className="mb-4 p-3 rounded-lg bg-emerald-50 border border-emerald-200 text-sm" data-testid="staff-import-result">
          <div className="flex items-center justify-between">
            <div>
              <strong className="text-emerald-800">Import complete.</strong>{' '}
              <span className="text-emerald-700">
                Created <strong>{importResult.created}</strong>, Updated <strong>{importResult.updated}</strong>,
                Skipped <strong>{importResult.skipped_invalid}</strong> (of {importResult.total_received}).
              </span>
            </div>
            <button
              onClick={() => setImportResult(null)}
              className="text-emerald-700 hover:text-emerald-900"
              data-testid="staff-import-result-close"
            >
              <X size={14} />
            </button>
          </div>
          {importResult.warnings && importResult.warnings.length > 0 && (
            <ul className="mt-2 ml-4 list-disc text-xs text-amber-800 space-y-1">
              {importResult.warnings.map((w, i) => (<li key={`warn-${String(w).slice(0, 40)}-${i}`}>{w}</li>))}
            </ul>
          )}
        </div>
      )}

      {view === 'tree' && (
        <StaffHierarchyTree />
      )}

      {view === 'table' && (
      <>
      <div className="mb-4 text-xs text-gray-600 bg-blue-50 border border-blue-100 rounded-lg px-3 py-2" data-testid="master-rate-note">
        Rate per kilometre applies to <strong>all staff &amp; admins</strong> from <strong>Master Settings</strong>
        {masterRate != null && <> — currently <strong>₹{masterRate}/km</strong></>}.
        Change it once in the <em>Settings</em> tab.
      </div>

      <FilterBar
        testId="staff-filters"
        placeholder="Search by name, mobile or staff ID…"
        search={search}
        onSearch={setSearch}
        chips={[
          { key: 'role', label: 'Role', value: roleFilter, options: [
            { value: 'admin', label: 'Admin' }, { value: 'staff', label: 'Staff' }, { value: 'gate', label: 'Gate' }
          ]},
          { key: 'status', label: 'Status', value: statusFilter, options: [
            { value: 'active', label: 'Active' }, { value: 'inactive', label: 'Inactive' }
          ]},
          { key: 'dept', label: 'Department', value: deptFilter,
            options: departments.map(d => ({ value: d.id, label: d.name })) },
          { key: 'desig', label: 'Designation', value: desigFilter,
            options: designations.map(d => ({ value: d.id, label: d.name })) },
          { key: 'sched', label: 'Schedule', value: scheduleFilter,
            options: schedules.map(s => ({ value: s.id, label: s.name })) },
          { key: 'geo', label: 'Geo-Fence', value: geoFilter,
            options: geofences.map(g => ({ value: g.id, label: g.name })) },
        ]}
        onChipChange={(k, v) => {
          if (k === 'role') setRoleFilter(v);
          else if (k === 'status') setStatusFilter(v);
          else if (k === 'dept') setDeptFilter(v);
          else if (k === 'desig') setDesigFilter(v);
          else if (k === 'sched') setScheduleFilter(v);
          else if (k === 'geo') setGeoFilter(v);
        }}
        onClear={() => {
          setSearch(''); setRoleFilter('all'); setStatusFilter('all');
          setDeptFilter('all'); setDesigFilter('all');
          setScheduleFilter('all'); setGeoFilter('all');
        }}
        resultCount={filteredStaff.length}
      />

      {showAdd && (
        <div className="bg-white rounded-xl p-6 shadow-sm border mb-6">
          <h3 className="font-semibold mb-4">Add New Staff Member</h3>
          <form onSubmit={handleAddStaff} className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <input data-testid="staff-name-input" type="text" placeholder="Staff Name" value={newStaff.name} onChange={(e) => setNewStaff({...newStaff, name: e.target.value})} className="px-3 py-2 border rounded-lg" required />
            <input data-testid="staff-mobile-input" type="tel" placeholder="Mobile (10 digits)" value={newStaff.mobile} onChange={(e) => setNewStaff({...newStaff, mobile: e.target.value})} className="px-3 py-2 border rounded-lg" required />
            <input data-testid="staff-id-input" type="text" placeholder="Staff ID" value={newStaff.staff_id} onChange={(e) => setNewStaff({...newStaff, staff_id: e.target.value})} className="px-3 py-2 border rounded-lg" required />
            <select
              data-testid="staff-geofence-select"
              value={newStaff.geo_fence_location_id}
              onChange={(e) => setNewStaff({ ...newStaff, geo_fence_location_id: e.target.value })}
              className="px-3 py-2 border rounded-lg bg-white md:col-span-3"
              required
            >
              <option value="">Select Geo-Fence Location…</option>
              {geofences.map(g => <option key={g.id} value={g.id}>{g.name}</option>)}
            </select>
            <div className="md:col-span-3 flex space-x-2">
              <button data-testid="staff-submit-btn" type="submit" className="px-4 py-2 bg-blue-600 text-white rounded-lg">Add (password = mobile)</button>
              <button type="button" onClick={() => setShowAdd(false)} className="px-4 py-2 bg-gray-200 rounded-lg">Cancel</button>
            </div>
          </form>
        </div>
      )}

      <div className="bg-white rounded-xl shadow-sm overflow-hidden" data-testid="staff-list">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Staff ID</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Mobile</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Role</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Department</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Designation</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Report 1</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Report 2</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Report 3</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Geo-Fence</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Rate/km<span className="ml-1 text-[10px] normal-case text-gray-400">(master)</span></th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Schedule</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {filteredStaff.map(s => {
                const isEditing = editing === s.id;
                const r1 = staff.find(x => x.id === (s.reports_to_1 || s.reporting_to));
                const r2 = staff.find(x => x.id === s.reports_to_2);
                const r3 = staff.find(x => x.id === s.reports_to_3);
                return (
                  <tr key={s.id} className="hover:bg-gray-50" data-testid={`staff-row-${s.id}`}>
                    <td className="px-4 py-2">
                      {isEditing ? (
                        <input value={editForm.name} onChange={(e) => setEditForm({...editForm, name: e.target.value})} className="px-2 py-1 border rounded w-full" />
                      ) : <span className="font-medium">{s.name}</span>}
                    </td>
                    <td className="px-4 py-2 text-gray-600">
                      {isEditing ? (
                        <input value={editForm.staff_id} onChange={(e) => setEditForm({...editForm, staff_id: e.target.value})} className="px-2 py-1 border rounded w-full" />
                      ) : (s.staff_id || '-')}
                    </td>
                    <td className="px-4 py-2 text-gray-600">
                      {isEditing && (s.role || '').toLowerCase() === 'admin' ? (
                        <input
                          type="tel"
                          value={editForm.mobile || ''}
                          onChange={(e) => setEditForm({ ...editForm, mobile: e.target.value })}
                          placeholder="10-digit mobile"
                          className="px-2 py-1 border rounded w-full"
                          data-testid={`edit-mobile-${s.id}`}
                          title="Admin mobile — used as login ID. Super Admin's mobile is locked."
                        />
                      ) : (
                        s.mobile
                      )}
                    </td>
                    <td className="px-4 py-2">
                      <span className={`px-2 py-0.5 text-xs rounded-full ${s.role === 'admin' ? 'bg-purple-100 text-purple-700' : 'bg-blue-100 text-blue-700'}`}>{s.role}</span>
                    </td>
                    {/* Department */}
                    <td className="px-4 py-2 text-gray-600 text-xs">
                      {isEditing ? (
                        <select
                          value={editForm.department_id || ''}
                          onChange={(e) => setEditForm({...editForm, department_id: e.target.value})}
                          className="px-2 py-1 border rounded w-full text-xs"
                          data-testid={`edit-department-${s.id}`}
                        >
                          <option value="">-- none --</option>
                          {departments.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
                        </select>
                      ) : (departments.find(d => d.id === s.department_id)?.name || '-')}
                    </td>
                    {/* Designation */}
                    <td className="px-4 py-2 text-gray-600 text-xs">
                      {isEditing ? (
                        <select
                          value={editForm.designation_id || ''}
                          onChange={(e) => setEditForm({...editForm, designation_id: e.target.value})}
                          className="px-2 py-1 border rounded w-full text-xs"
                          data-testid={`edit-designation-${s.id}`}
                        >
                          <option value="">-- none --</option>
                          {designations.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
                        </select>
                      ) : (designations.find(d => d.id === s.designation_id)?.name || '-')}
                    </td>
                    {/* Report 1 / 2 / 3 */}
                    {[
                      ['reports_to_1', r1],
                      ['reports_to_2', r2],
                      ['reports_to_3', r3],
                    ].map(([key, person]) => (
                      <td key={key} className="px-4 py-2 text-gray-600">
                        {isEditing ? (
                          <select
                            value={editForm[key] || ''}
                            onChange={(e) => setEditForm({...editForm, [key]: e.target.value})}
                            className="px-2 py-1 border rounded w-full text-xs"
                            data-testid={`edit-${key}-${s.id}`}
                          >
                            <option value="">-- none --</option>
                            {staff.filter(x => x.id !== s.id).map(x => <option key={x.id} value={x.id}>{x.name}</option>)}
                          </select>
                        ) : (person ? person.name : '-')}
                      </td>
                    ))}
                    <td className="px-4 py-2 text-gray-600 text-xs">
                      {isEditing ? (
                        <select
                          value={editForm.geo_fence_location_id || ''}
                          onChange={(e) => setEditForm({ ...editForm, geo_fence_location_id: e.target.value })}
                          className="px-2 py-1 border rounded w-full text-xs bg-white"
                          data-testid={`edit-geofence-${s.id}`}
                        >
                          <option value="">-- none --</option>
                          {geofences.map(g => <option key={g.id} value={g.id}>{g.name}</option>)}
                        </select>
                      ) : (
                        geofences.find(g => g.id === s.geo_fence_location_id)?.name || s.geo_fence_location_id || '-'
                      )}
                    </td>
                    <td className="px-4 py-2 text-gray-600">
                      <span className="text-gray-700">₹{masterRate ?? '—'}</span>
                    </td>
                    <td className="px-4 py-2">
                      {isEditing ? (
                        <select
                          value={editForm.schedule_id || ''}
                          onChange={(e) => setEditForm({ ...editForm, schedule_id: e.target.value })}
                          className="px-2 py-1 border rounded w-full text-xs"
                          data-testid={`edit-schedule-${s.id}`}
                        >
                          <option value="">-- none --</option>
                          {schedules.map(sc => <option key={sc.id} value={sc.id}>{sc.name}</option>)}
                        </select>
                      ) : (
                        <div className="flex items-center gap-2">
                          <span className="text-xs font-medium text-gray-700">
                            {schedules.find(sc => sc.id === s.schedule_id)?.name || (
                              <span className="text-gray-400 italic">Custom</span>
                            )}
                          </span>
                          <button
                            onClick={() => setScheduleStaff(s)}
                            className="flex items-center gap-0.5 text-[11px] text-blue-700 hover:text-blue-900 underline"
                            data-testid={`open-schedule-${s.id}`}
                            title="Fine-tune this staff's schedule (overrides the master)"
                          >
                            <Clock size={11} /> {s.day_checkin_time || '09:00'}–{s.day_checkout_time || '20:00'}
                          </button>
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-2">
                      {isEditing ? (
                        <select value={editForm.status} onChange={(e) => setEditForm({...editForm, status: e.target.value})} className="px-2 py-1 border rounded">
                          <option value="active">active</option>
                          <option value="inactive">inactive</option>
                        </select>
                      ) : (
                        <span className={`px-2 py-0.5 text-xs rounded-full ${s.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-gray-200 text-gray-700'}`}>{s.status}</span>
                      )}
                    </td>
                    <td className="px-4 py-2 text-right whitespace-nowrap">
                      {isEditing ? (
                        <>
                          <button onClick={() => saveEdit(s.id)} className="text-green-600 hover:text-green-700 p-1 mx-0.5" data-testid={`save-staff-${s.id}`} title="Save"><Save size={16} /></button>
                          <button onClick={() => setEditing(null)} className="text-gray-600 hover:text-gray-700 p-1 mx-0.5" title="Cancel"><X size={16} /></button>
                        </>
                      ) : (
                        <>
                          <button onClick={() => startEdit(s)} className="text-blue-600 hover:text-blue-700 p-1 mx-0.5" data-testid={`edit-staff-${s.id}`} title="Edit"><Edit2 size={16} /></button>
                          <button
                            onClick={() => resetPasswordToMobile(s)}
                            className="text-amber-600 hover:text-amber-700 p-1 mx-0.5"
                            data-testid={`reset-password-${s.id}`}
                            title="Reset password to mobile number"
                          >
                            <KeyRound size={16} />
                          </button>
                          <button onClick={() => deleteStaff(s)} className="text-red-600 hover:text-red-700 p-1 mx-0.5" data-testid={`delete-staff-${s.id}`} title="Delete"><Trash2 size={16} /></button>
                        </>
                      )}
                    </td>
                  </tr>
                );
              })}
              {filteredStaff.length === 0 && (
                <tr><td colSpan="14" className="px-4 py-8 text-center text-gray-500">{staff.length === 0 ? 'No staff members yet' : 'No staff match the current filter'}</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
      </>
      )}

      {scheduleStaff && (
        <StaffScheduleModal
          staff={scheduleStaff}
          onClose={() => setScheduleStaff(null)}
          onSaved={() => fetchStaff()}
        />
      )}
    </div>
  );
}
